/**
 * Settings → Document Shares (T-144 / SP-B, PR#4).
 *
 * The management screen for production document shares: every bundle a staff
 * member has sent, its scope (production), expiry and status, and per-recipient
 * last-download + count — with the ability to revoke a whole bundle or a single
 * recipient's link. This is the launch blocker for the long-lived (up to 1-year)
 * capability links: once it ships, the sharing email route can be turned on.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, ChevronDown, ChevronRight, ShieldOff, Share2 } from 'lucide-react';
import { fetchDocumentShares, revokeDocumentShare, type ShareBundle } from '@/lib/documentsApi';

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-[#8FB560]/15 text-[#5f7d3a] dark:text-[#a9cd7f]',
  expired: 'bg-muted text-muted-foreground',
  revoked: 'bg-[#E0556C]/15 text-[#b23145] dark:text-[#e88b98]',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[status] ?? 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** A two-click revoke: first click arms, second confirms. Avoids a modal. */
function RevokeButton({ label, onRevoke, busy }: { label: string; onRevoke: () => void; busy: boolean }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => (armed ? onRevoke() : setArmed(true))}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
        armed
          ? 'border-[#E0556C] bg-[#E0556C] text-white'
          : 'border-[#E0556C]/40 text-[#b23145] hover:bg-[#E0556C]/10 dark:text-[#e88b98]'
      }`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldOff className="h-3.5 w-3.5" />}
      {armed ? 'Confirm revoke?' : label}
    </button>
  );
}

export function SettingsDocumentShares() {
  const [state, setState] = useState<{ status: 'loading' | 'error' } | { status: 'ok'; bundles: ShareBundle[] }>({ status: 'loading' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { bundles } = await fetchDocumentShares();
      setState({ status: 'ok', bundles });
    } catch {
      setState({ status: 'error' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const revoke = async (bundleId: string, recipientId?: string) => {
    setBusyId(recipientId ?? bundleId);
    setRevokeError(null);
    try {
      await revokeDocumentShare(bundleId, recipientId);
      await load();
    } catch {
      // A failed revoke leaves the link LIVE — surface it, don't swallow it.
      setRevokeError("Couldn't revoke that share — it may still be live. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-4 md:p-6">
      <header className="mb-4 flex items-center gap-2">
        <Share2 className="h-5 w-5 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">Document Shares</h1>
          <p className="text-sm text-muted-foreground">
            Production document links you've sent. Revoke a whole share or a single recipient at any time.
          </p>
        </div>
      </header>

      {revokeError && (
        <div role="alert" className="mb-3 rounded-md border border-[#E0556C]/40 bg-[#E0556C]/10 px-3 py-2 text-sm text-[#b23145] dark:text-[#e88b98]">
          {revokeError}
        </div>
      )}

      {state.status === 'loading' && (
        <div className="flex items-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading shares…
        </div>
      )}

      {state.status === 'error' && (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          Couldn't load document shares. <button className="text-[#8FB560] hover:underline" onClick={() => { setState({ status: 'loading' }); void load(); }}>Try again</button>.
        </div>
      )}

      {state.status === 'ok' && state.bundles.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
          No document shares yet. Shares you send will appear here.
        </div>
      )}

      {state.status === 'ok' && state.bundles.length > 0 && (
        <ul className="space-y-2">
          {state.bundles.map((b) => {
            const isOpen = expanded.has(b.id);
            const activeRecipients = b.recipients.filter((r) => r.status === 'active').length;
            return (
              <li key={b.id} className="rounded-lg border border-border bg-card">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button type="button" onClick={() => toggle(b.id)} className="text-muted-foreground hover:text-foreground" aria-label={isOpen ? 'Collapse' : 'Expand'}>
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{b.production_name ?? 'Production documents'}</span>
                      <StatusBadge status={b.status} />
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                      {b.recipients.length} recipient{b.recipients.length === 1 ? '' : 's'} · {activeRecipients} active · expires {fmtDate(b.expires_at)}
                    </div>
                  </div>
                  {b.status === 'active' && (
                    <RevokeButton label="Revoke share" busy={busyId === b.id} onRevoke={() => revoke(b.id)} />
                  )}
                </div>

                {isOpen && (
                  <div className="border-t border-border overflow-x-auto">
                    <table className="w-full min-w-[560px] text-sm">
                      <thead>
                        <tr className="text-left text-xs text-muted-foreground">
                          <th className="px-4 py-2 font-medium">Recipient</th>
                          <th className="px-4 py-2 font-medium">Last download</th>
                          <th className="px-4 py-2 font-medium text-right">Downloads</th>
                          <th className="px-4 py-2 font-medium text-right">Status</th>
                          <th className="px-4 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {b.recipients.map((r) => (
                          <tr key={r.id} className="border-t border-border/60">
                            <td className="px-4 py-2">
                              <div className="font-medium">{r.contact_name ?? '—'}</div>
                              <div className="text-xs text-muted-foreground">{r.email ?? r.role_key ?? ''}</div>
                            </td>
                            <td className="px-4 py-2 text-muted-foreground tabular-nums">{fmtDate(r.last_downloaded_at)}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{r.download_count}</td>
                            <td className="px-4 py-2 text-right"><StatusBadge status={r.status} /></td>
                            <td className="px-4 py-2 text-right">
                              {r.status === 'active' && b.status === 'active' && (
                                <RevokeButton label="Revoke" busy={busyId === r.id} onRevoke={() => revoke(b.id, r.id)} />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default SettingsDocumentShares;
